/** @jsxImportSource @emotion/react */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
} from '@mui/material';
import { tb } from 'components/layouts/Table';
import { useTranslation } from 'react-i18next';
import { MdIvestMdlEquipmentReviewResult } from 'models/md/MdInvestResult';
import CustomInputWithSearch from 'components/inputs/CustomTextarea';
import {
  getMdIvestMdlEquipmentReviewResult,
  saveMdIvestMdlEquipmentReviewResult,
} from 'apis/md/MdInvestResult';

interface Props {
  elmPrjId?: string;
  ivesMdlId?: string;
  isReadOnly?: boolean;
  data?: MdIvestMdlEquipmentReviewResult[];
  setData: Dispatch<SetStateAction<MdIvestMdlEquipmentReviewResult[]>>;
}

const EquipmentReviewTable = (
  { elmPrjId, ivesMdlId, isReadOnly = false, data = [], setData, ...props }: Props,
  ref
) => {
  const { t } = useTranslation();
  const [localData, setLocalData] = useState<MdIvestMdlEquipmentReviewResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!elmPrjId || !ivesMdlId) {
        setLocalData([]);
        return;
      }

      setLoading(true);
      try {
        const result = await getMdIvestMdlEquipmentReviewResult(elmPrjId, ivesMdlId);
        setLocalData(result);
        if (setData) {
          setData(result);
        }
      } catch (error) {
        console.error('Failed to load equipment review data:', error);
        setLocalData([]);
        if (setData) {
          setData([]);
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [elmPrjId, ivesMdlId, setData]);

  useImperativeHandle(ref, () => ({
    search: (elmPrjId, ivesMdlId) => {
      handleSearch(elmPrjId, ivesMdlId);
    },
    save: (showMsg = true) => {
      return new Promise((resolve) => {
        handleSave().then((res) => {
          resolve(res);
        });
      });
    },
    // 데이터가 로드되었는지 확인하는 메서드 추가
    isDataLoaded: () => {
      return !loading && localData.length > 0;
    },
  }));

  const handleSearch = async (elmPrjId, ivesMdlId) => {
    const result = await getMdIvestMdlEquipmentReviewResult(elmPrjId, ivesMdlId);
    setData(result);
    setLocalData(result);
  };

  /**
   * 저장 핸들러
   */
  const handleSave = useCallback(async () => {
    return new Promise((resolve) => {
      saveMdIvestMdlEquipmentReviewResult(elmPrjId, ivesMdlId, localData).then((res) => {
        resolve(res);
      });
    });
  }, [elmPrjId, ivesMdlId, localData]);

  /**
   * 검토내용/비고 변경 핸들러
   */
  const handleChange = (eqclId: string, field: 'rvwRltCtn' | 'rvwRltRmk', value: string) => {
    setLocalData((prevData) =>
      prevData.map((item) => (item.eqclId === eqclId ? { ...item, [field]: value } : item))
    );
    // 부모의 setData도 호출
    if (setData) {
      setData((prevData) =>
        prevData.map((item) => (item.eqclId === eqclId ? { ...item, [field]: value } : item))
      );
    }
  };

  /**
   * Spec-In/Spec-Out에 따른 색상 반환
   */
  const getResultColor = (result?: string) => {
    if (result === 'Spec-In') return 'success';
    if (result === 'Spec-Out') return 'error';
    return 'default';
  };

  /**
   * 설비군별 행 렌더링
   */
  const renderRow = (item: MdIvestMdlEquipmentReviewResult) => {
    const isKeyIn = item.rvwMethod === 'KEY-IN';
    const resultColor = getResultColor(item.calcRvwRltCtn);

    const textareaStyle = {
      height: '27px',
      fontSize: '13px',
      lineHeight: '1.4',
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'none', // Firefox
      msOverflowStyle: 'none', // IE and Edge
      '&::-webkit-scrollbar': {
        display: 'none', // Chrome, Safari, Edge
      },
    };

    // KEY-IN 방식
    if (isKeyIn) {
      return (
        <TableRow key={item.eqclId}>
          <TableCell colSpan={3} sx={{ fontWeight: 'bold !important' }}>
            {item.eqclNm}
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltCtn}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(
                  t('md.msg.설비사양검토를 입력해 주세요.', '설비사양검토를 입력해 주세요.')
                )}
                textareaStyle={textareaStyle}
                value={item.rvwRltCtn || ''}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltCtn', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltRmk}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(t('com.msg.비고를 입력해 주세요.', '비고를 입력해 주세요.'))}
                textareaStyle={textareaStyle}
                value={item.rvwRltRmk || ''}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltRmk', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
        </TableRow>
      );
    }

    // ROL (롤프레스)
    if (item.eqclId === 'ROL') {
      return (
        <TableRow key={item.eqclId}>
          <TableCell colSpan={3} sx={{ fontWeight: 'bold !important' }}>
            {item.eqclNm}
          </TableCell>
          <TableCell colSpan={3}>
            <Box>{item.calcRvwRltCtn}</Box>
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltRmk}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(t('com.msg.비고를 입력해 주세요.', '비고를 입력해 주세요.'))}
                value={item.rvwRltRmk || ''}
                textareaStyle={textareaStyle}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltRmk', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
        </TableRow>
      );
    }

    // N&D (DNC)
    if (item.eqclId === 'NND') {
      return (
        <TableRow key={item.eqclId}>
          <TableCell colSpan={3} sx={{ fontWeight: 'bold !important' }}>
            {item.eqclNm}
          </TableCell>
          <TableCell colSpan={3}>
            <Box>{item.calcRvwRltCtn}</Box>
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltRmk}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(t('com.msg.비고를 입력해 주세요.', '비고를 입력해 주세요.'))}
                value={item.rvwRltRmk || ''}
                textareaStyle={textareaStyle}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltRmk', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
        </TableRow>
      );
    }

    // L&S or S&F or AZS/DNC (LNS, SNF, AZSDNC)
    if (['LNS', 'SNF', 'AZSDNC'].includes(item?.eqclId ?? '')) {
      return (
        <TableRow key={item.eqclId}>
          <TableCell colSpan={3} sx={{ fontWeight: 'bold !important' }}>
            {item.eqclNm}
          </TableCell>
          <TableCell colSpan={3}>
            <Box>{item.calcRvwRltCtn}</Box>
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltRmk}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(t('com.msg.비고를 입력해 주세요.', '비고를 입력해 주세요.'))}
                value={item.rvwRltRmk || ''}
                textareaStyle={textareaStyle}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltRmk', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
        </TableRow>
      );
    }

    // PKG
    if (item.eqclId === 'PKG') {
      return (
        <TableRow key={item.eqclId}>
          <TableCell colSpan={3} sx={{ fontWeight: 'bold !important' }}>
            {item.eqclNm}
          </TableCell>
          <TableCell colSpan={3}>
            <Box>{item.calcRvwRltCtn}</Box>
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltRmk}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(t('com.msg.비고를 입력해 주세요.', '비고를 입력해 주세요.'))}
                value={item.rvwRltRmk || ''}
                textareaStyle={textareaStyle}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltRmk', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
        </TableRow>
      );
    }

    // JIG / FOR (J/Formation)
    if (item.eqclId === 'JIG' || item.eqclId === 'FOR') {
      return (
        <TableRow key={item.eqclId}>
          <TableCell colSpan={3} sx={{ fontWeight: 'bold !important' }}>
            {item.eqclNm}
          </TableCell>
          <TableCell colSpan={3}>
            <Box>{item.calcRvwRltCtn}</Box>
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltRmk}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(t('com.msg.비고를 입력해 주세요.', '비고를 입력해 주세요.'))}
                value={item.rvwRltRmk || ''}
                textareaStyle={textareaStyle}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltRmk', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
        </TableRow>
      );
    }

    // DGS
    if (item.eqclId === 'DGS') {
      return (
        <TableRow key={item.eqclId}>
          <TableCell colSpan={3} sx={{ fontWeight: 'bold !important' }}>
            {item.eqclNm}
          </TableCell>
          <TableCell colSpan={3}>
            <Box>{item.calcRvwRltCtn}</Box>
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltRmk}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(t('com.msg.비고를 입력해 주세요.', '비고를 입력해 주세요.'))}
                value={item.rvwRltRmk || ''}
                textareaStyle={textareaStyle}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltRmk', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
        </TableRow>
      );
    }

    // SFEOL (EOL)
    if (item.eqclId === 'SFEOL') {
      return (
        <TableRow key={item.eqclId}>
          <TableCell colSpan={3} sx={{ fontWeight: 'bold !important' }}>
            {item.eqclNm}
          </TableCell>
          <TableCell colSpan={3}>
            <Box>{item.calcRvwRltCtn}</Box>
          </TableCell>
          <TableCell colSpan={3}>
            {isReadOnly ? (
              <Box>{item.rvwRltRmk}</Box>
            ) : (
              <CustomInputWithSearch
                placeholder={String(t('com.msg.비고를 입력해 주세요.', '비고를 입력해 주세요.'))}
                value={item.rvwRltRmk || ''}
                textareaStyle={textareaStyle}
                onChange={(e) => handleChange(item?.eqclId ?? '', 'rvwRltRmk', e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  };

  return (
    <>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" p={2}>
          <CircularProgress size={24} />
          <Box ml={1} fontSize="14px">
            {t('com.msg.데이터를 불러오는 중...', '데이터를 불러오는 중...')}
          </Box>
        </Box>
      )}
      <TableContainer css={tb.tableColDynamic(56)}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={3} sx={{ width: '15%' }}>
                설비군
              </TableCell>
              <TableCell colSpan={3} sx={{ width: '35%' }}>
                설비 사양 검토
              </TableCell>
              <TableCell colSpan={3} sx={{ width: '50%' }}>
                비고
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{(localData || []).map((item) => renderRow(item))}</TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default React.forwardRef(EquipmentReviewTable);
